import { useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { IPhone } from './iphone';
import { AppHomeScreen } from './app-home-screen';

// How far down the screen the band's top has to rise before the phone comes in.
const ARRIVE_LINE = 0.35;

/**
 * Writes to the node directly, so nothing re-renders:
 * - `data-staged` takes the hidden state over from the CSS-only first paint, whose 4 s fallback shows the phone without JS.
 * - `data-arrived` when the band's top passes `ARRIVE_LINE`, or at once if the whole band fits: the phone spins in.
 * - `data-offscreen` stops the screen's loop outright.
 */
function usePhoneStage<T extends HTMLElement>() {
	const ref = useRef<T>(null);
	useEffect(() => {
		const node = ref.current;
		const band = node?.closest('section');
		if (!node || !band) return;
		node.setAttribute('data-staged', '');

		const arrival: IntersectionObserver[] = [];
		const arrive = () => {
			node.setAttribute('data-arrived', '');
			for (const observer of arrival) observer.disconnect();
		};
		// The root ends at the arrival line, so the band enters it when its top crosses the line.
		arrival.push(
			new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.isIntersecting)) arrive();
				},
				{ rootMargin: `0px 0px -${Math.round((1 - ARRIVE_LINE) * 100)}% 0px` },
			),
		);
		// A screen tall enough for the whole band brings it in without a scroll.
		arrival.push(
			new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.intersectionRatio >= 0.99)) arrive();
				},
				{ threshold: [0.99] },
			),
		);
		for (const observer of arrival) observer.observe(band);

		const offscreen = new IntersectionObserver((entries) => {
			for (const entry of entries) node.toggleAttribute('data-offscreen', !entry.isIntersecting);
		});
		offscreen.observe(node);

		return () => {
			for (const observer of arrival) observer.disconnect();
			offscreen.disconnect();
		};
	}, []);
	return ref;
}

/** The screen is laid out at 300 x 637 px and zoomed, so the two widths below move with the zoom in `AppHomeScreen`. */
export function AppPhone() {
	const { t } = useLingui();
	const ref = usePhoneStage<HTMLDivElement>();
	return (
		<div ref={ref} className="app-phone relative">
			{/* The floor shadow stays flat while the phone turns above it. */}
			<span aria-hidden className="absolute inset-x-4 -bottom-1 h-8 rounded-[50%] bg-black/25 blur-xl" />
			<IPhone label={t`Worldcoo app preview`} className="[--phone-w:12.5rem] md:[--phone-w:19rem]">
				<AppHomeScreen />
			</IPhone>
		</div>
	);
}
